<script setup lang="ts">
  import { computed } from 'vue';
  import type { HTMLAttributes } from 'vue';

  import { ComboSelect } from '@ui/components/combo-select';

  import { toPartnerRef, usePartnerSelect } from '@/composables/usePartnerSelect';
  import type { PartnerRef, SelectPartner } from '@/composables/usePartnerSelect';

  interface Props {
    btnClass?: HTMLAttributes['class'];
    btnSize?: string;
    partnerId?: string | null;
    queryName?: string;
  }

  const props = withDefaults(defineProps<Props>(), { partnerId: undefined, queryName: 'customer' });

  const emit = defineEmits<{
    (e: 'update', value: PartnerRef | null): void;
  }>();

  const { selectedPartner, isLoading, partnerLoader } = usePartnerSelect({
    partnerId: () => props.partnerId,
    queryName: props.queryName,
  });

  // While loading a partnerId, pass a sentinel so ComboSelect's #selected slot renders
  // (it only renders when modelValue is non-null). The slot shows the spinner, not the value.
  const comboValue = computed({
    get: () =>
      isLoading.value && !selectedPartner.value ? { id: '', name: null } : selectedPartner.value,
    set: (v) => {
      selectedPartner.value = v as typeof selectedPartner.value;
    },
  });

  const clearSelection = () => {
    selectedPartner.value = null;
  };

  defineExpose({
    selectedCustomer: selectedPartner,
    clearSelection,
  });
</script>

<template>
  <ComboSelect
    v-model="comboValue"
    :loader="partnerLoader"
    :btn-class="btnClass"
    searchable
    variant="filter"
    placeholder="Filter By Customer"
    search-placeholder="Filter by customer..."
    section-heading="Customers"
    option-identifier="id"
    :btn-size="btnSize"
    dropdown-class="max-w-80"
    @update:model-value="
      (customer) =>
        emit(
          'update',
          customer && !Array.isArray(customer) ? toPartnerRef(customer as SelectPartner) : null,
        )
    "
  >
    <template #filter-label>
      <i class="fas fa-id-badge text-xs" />
      <span> Customer </span>
    </template>

    <template #option="{ option }">
      <span class="flex items-center justify-between w-full gap-2">
        <span class="truncate">{{ option.name }}</span>
        <span
          v-if="toPartnerRef(option as SelectPartner).accountNumber"
          class="text-muted-foreground shrink-0"
        >
          {{ toPartnerRef(option as SelectPartner).accountNumber }}
        </span>
      </span>
    </template>

    <template #selected="{ value }">
      <i v-if="isLoading" class="fa fa-spinner-third animate-spin" />
      <template v-else>
        {{ value?.name ?? '' }}
      </template>
    </template>
  </ComboSelect>
</template>
